/**
 * Analytics Helper
 *
 * Centralized event tracking for the post-design funnel.
 * Dispatches CustomEvents and forwards key funnel events to platform analytics.
 */

import { trackPlatformEvent, ensureSessionStarted, type PlatformEventType } from './platformAnalytics';

ensureSessionStarted();

export type AnalyticsEvent = 
  | 'design.complete.view'
  | 'preview.open'
  | 'preview.side.change'
  | 'preview.order_click'
  | 'preview.customize_drawer_open'
  | 'preview.product_select'
  | 'preview.size_select'
  | 'preview.color_select'
  | 'preview.add_to_cart'
  | 'preview.share'
  | 'preview.like'
  | 'options.view'
  | 'options.variant.change'
  | 'options.qty.change'
  | 'options.continue_checkout_click'
  | 'cart.view'
  | 'cart.qty.change'
  | 'cart.item.remove'
  | 'cart.checkout.click'
  | 'cart.design_another.click'
  | 'cart.item.added'
  | 'cart.merged'
  | 'checkout.view'
  | 'checkout.payment.select'
  | 'checkout.place_order_click'
  | 'checkout.payment_succeeded'
  | 'checkout.order_created'
  | 'checkout.error'
  | 'checkout.gate.shown'
  | 'checkout.returning_user_detected'
  | 'checkout.returning_user_signin_click'
  | 'checkout.returning_user_welcome_shown'
  | 'checkout.inline_login_shown'
  | 'checkout.inline_login_success'
  | 'checkout.inline_login_failed'
  | 'checkout.inline_login_attempt'
  | 'order.success.view'
  | 'order.share.click'
  | 'order.create_account.click'
  | 'order.create_account.dismiss'
  | 'draft.saved'
  | 'draft.restored'
  | 'draft.discarded'
  | 'draft.claimed'
  | 'login.success.redirected'
  | 'conflict.shown'
  | 'conflict.resolved'
  | 'offline.save'
  | 'sync.reconnect'
  // ============================================
  // Auth / Google funnel events
  // ============================================
  | 'auth.google.click'
  | 'auth.google.success'
  | 'auth.google.fail'
  | 'auth.one_tap.prompt'
  | 'auth.one_tap.accept'
  | 'auth.one_tap.dismiss'
  | 'auth.one_tap.fail'
  | 'auth.linked_accounts.view'
  | 'auth.linked_accounts.link_click'
  | 'auth.linked_accounts.link_success'
  | 'auth.linked_accounts.unlink'
  | 'auth.linked_accounts.set_password'
  | 'auth.prompt.shown'
  | 'auth.prompt.dismiss'
  | 'auth.prompt.success'
  | 'auth.prompt.email_expand';

interface AnalyticsPayload {
  [key: string]: any;
}

class Analytics {
  private static instance: Analytics;
  private eventQueue: Array<{ event: AnalyticsEvent; payload?: AnalyticsPayload; timestamp: number }> = [];

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  /**
   * Track an event
   * Fires exactly once per action
   */
  track(event: AnalyticsEvent, payload?: AnalyticsPayload): void {
    const eventData = {
      event,
      payload,
      timestamp: Date.now(),
    };

    this.eventQueue.push(eventData);

    if (import.meta.env.DEV) {
      console.log('[Analytics]', event, payload || '');
    }

    globalThis.dispatchEvent(
      new CustomEvent('qumashi:analytics', {
        detail: eventData,
      })
    );

    this.forwardToPlatform(event, payload);
  }

  private forwardToPlatform(event: AnalyticsEvent, payload?: AnalyticsPayload): void {
    const map: Partial<Record<AnalyticsEvent, PlatformEventType>> = {
      'preview.add_to_cart': 'ADD_TO_CART',
      'cart.item.added': 'ADD_TO_CART',
      'cart.item.remove': 'ADD_TO_CART',
      'cart.qty.change': 'ADD_TO_CART',
      'cart.checkout.click': 'CHECKOUT_STARTED',
      'checkout.view': 'CHECKOUT_STARTED',
      'checkout.place_order_click': 'PAYMENT_ATTEMPTED',
      'checkout.payment_succeeded': 'PAYMENT_SUCCEEDED',
      'checkout.order_created': 'ORDER_PLACED',
      'order.success.view': 'ORDER_PLACED',
    };
    if (event === 'checkout.payment.select') {
      trackPlatformEvent({
        eventType: 'PAYMENT_ATTEMPTED',
        orderId: payload?.orderId,
        metadata: { paymentMethod: payload?.method },
      });
      return;
    }
    const platformType = map[event];
    if (!platformType) return;
    trackPlatformEvent({
      eventType: platformType,
      productId: payload?.productId,
      listingId: payload?.listingId,
      designId: payload?.designId,
      orderId: payload?.orderId,
      sellerId: payload?.sellerId,
      creatorProfileId: payload?.creatorProfileId,
      metadata: payload,
    });
  }

  /**
   * Get event history (useful for debugging)
   */
  getEventQueue(): typeof this.eventQueue {
    return [...this.eventQueue];
  }

  /**
   * Clear event queue
   */
  clearQueue(): void {
    this.eventQueue = [];
  }
}

// Export singleton instance
export const analytics = Analytics.getInstance();

// Export convenience function
export const trackEvent = (event: AnalyticsEvent, payload?: AnalyticsPayload) => {
  analytics.track(event, payload);
};

